//----------------------------------------------------------------------
import { Helmet } from "react-helmet-async";
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Card,
  Container,
  Typography,
  Button,
  MenuItem,
  Stack,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Tabs,
  Tab,
  TextField,
  Divider,
  Alert,
} from "@mui/material";

import { Edit, LinkOff } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CustomTable from "../components/data-table/CustomDataTable";
import {
  useDelinkUserMutation,
  useGetSupportUsersQuery,
  useGetUsersQuery,
  useGetUsersWithoutGAQuery,
  useLazyGetSupportUsersQuery,
  useLazyGetUsersQuery,
  useLazyGetUsersWithoutGAQuery,
  useLazyGetLazySupportUserBySearchQuery,
} from "../redux/services/user.service";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";

import Protected from "../protected/Protected";
import LinkCodeForm from "../sections/@dashboard/user/linkCode.form";
import BulkAddUserForm from "../sections/@dashboard/user/bulkAddUser.form";
import UserForm from "../sections/@dashboard/user/user.form";
import { useGetOrganizationsQuery } from "../redux/services/organization.service";
import { selectAbility } from "../redux/slices/casl.slice";
import { selectUser } from "../redux/slices/auth.slice";
import ChangeMobileForm from "../sections/@dashboard/user/changeMobile.form";
import CustomDeleteConfirmation from "../components/dialog/CustomDeleteConfirmation";
import { useGetEventPlacesQuery } from "../redux/services/device.service";
import useDownloadAll from "../hooks/useDownloadAll";

export default function UserPage() {
  const ability = useSelector(selectAbility);
  const user = useSelector(selectUser);
  const roles = user?.roles?.map((r) => r.name) || [];
  const isSupport = roles.includes("support");

  // Tab state for switching between With Analytics and Without Analytics
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [openRow, setOpenRow] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(29, "day"));
  const [endDate, setEndDate] = useState(dayjs());

  const [openLinkCodeForm, setOpenLinkCodeForm] = useState(false);

  const [openAddBulkUserForm, setOpenAddBulkUserForm] = useState(false);

  const [openUserForm, setOpenUserForm] = useState(false);

  const [openChangeMobileForm, setOpenChangeMobileForm] = useState(false);

  const [openDelinkConfirmation, setOpenDelinkConfirmation] = useState(false);

  // Support-specific filter state
  const [supportSubscriptionStatus, setSupportSubscriptionStatus] = useState("");
  const [supportUserStatus, setSupportUserStatus] = useState("");

  const navigate = useNavigate();

  const [delink, { isLoading: unlinkCodeLoading }] = useDelinkUserMutation();

  // Support user search state
  const [supportSearchInput, setSupportSearchInput] = useState("");
  const [searchedUser, setSearchedUser] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [triggerSupportSearch, { isFetching: isSupportSearchFetching, error: supportSearchError }] =
    useLazyGetLazySupportUserBySearchQuery();

  // Download all hooks
  const {
    handleDownloadAll: handleDownloadAllUsers,
    isDownloading: isDownloadingUsers,
  } = useDownloadAll(useLazyGetUsersQuery, "Users");
  const {
    handleDownloadAll: handleDownloadAllUsersWithoutGA,
    isDownloading: isDownloadingUsersWithoutGA,
  } = useDownloadAll(useLazyGetUsersWithoutGAQuery, "UsersWithoutGA");
  const {
    handleDownloadAll: handleDownloadAllSupportUsers,
    isDownloading: isDownloadingSupportUsers,
  } = useDownloadAll(useLazyGetSupportUsersQuery, "SupportUsers");

  const [selectedEventPlace, setSelectedEventPlace] = useState("");
  const [filteredTags, setFilteredTags] = useState([]); // State to store filtered tags

  const {
    register,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      organizationId: "",
      userType: "all",
    },
  });

  // Set organization ID if the user has organization role
  useEffect(() => {
    if (ability.can("Is", "organization")) {
      const orgId = localStorage.getItem("organizationId");
      if (orgId) {
        setValue("organizationId", orgId);
      }
    }
  }, [ability, setValue]);

  const {
    isLoading: isOrgLoading,
    isFetching: isOrgFetching,
    data: organizations,
  } = useGetOrganizationsQuery(null, {});

  const {
    data: eventPlaces,
    isSuccess: isSuccessEventPlaces,
    isLoading: isLoadingEventPlaces,
  } = useGetEventPlacesQuery(
    {
      organizationId: watch("organizationId"),
    },
    {
      // Using RTK Query's cache management to improve performance
      refetchOnMountOrArgChange: true, // Only refetch when dependencies change
      refetchOnFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true, // Refetch on network reconnection
    },
  );

  const handleLinkCode = () => {
    setOpenLinkCodeForm(true);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    console.log("End Date:", date);
    setEndDate(date);
  };

  const handleResetFilter = () => {
    setValue("userType", "all");
    setValue("organizationId", "");
    setSelectedEventPlace("");
    setValue("tag", "");
    setValue("deployDates", []);
    setStartDate(dayjs().subtract(29, "day"));
    setEndDate(dayjs());
    setFilteredTags([]); // Also reset the filtered tags
    // Reset support-specific filters
    setSupportSubscriptionStatus("");
    setSupportUserStatus("");
  };

  const handleEventPlaceChange = (event) => {
    try {
      const selectedPlace = event.target.value;
      setSelectedEventPlace(selectedPlace);

      // Filter tags based on selected event place
      const filteredPlaces = getFilteredEventPlaces();
      const selectedTags =
        filteredPlaces.filter(
          (event) => event.eventPlace === selectedPlace && event.tags !== null,
        )[0]?.tags || [];

      console.log("Selected Tags:", selectedTags);
      setFilteredTags(selectedTags);

      // Reset the tag field (clear the value)
      setValue("tag", ""); // This will clear the tag field
    } catch (error) {
      // Catch and log any errors
      console.error("Error while handling event place change:", error);
    }
  }; // Function to get filtered event places based on user type

  // Function to get filtered event places based on user type
  const getFilteredEventPlaces = () => {
    if (!eventPlaces?.data) return [];

    let list = [];
    const userType = watch("userType");

    if (!userType || userType === "all") {
      list = eventPlaces.data.allEventPlaces || [];
    } else if (userType === "device") {
      list = eventPlaces.data.deviceEventPlaces || [];
    } else if (userType === "subscription") {
      list = eventPlaces.data.subscriptionEventPlaces || [];
    } else {
      list = eventPlaces.data.allEventPlaces || [];
    }

    return list.filter(
      (place) =>
        place?.eventPlace &&
        typeof place.eventPlace === "string" &&
        place.eventPlace.trim() !== ""
    );
  };

  // Memoizing the query parameters
  const watchedUserType = watch("userType");
  const watchedOrgId = watch("organizationId");
  const watchedTag = watch("tag");
  const watchedDistributorId = watch("distributorId");

  // Reset eventPlace and tags when userType changes
  useEffect(() => {
    // Reset selectedEventPlace and tag when userType changes
    setSelectedEventPlace("");
    setValue("tag", "");
    setFilteredTags([]);
  }, [watchedUserType, setValue]);

  const queryParams = useMemo(() => {
    console.log("Query Params:", startDate, endDate);
    const query = {};
    if (watchedUserType) query.userType = watchedUserType;
    if (watchedOrgId) query.organizationId = watchedOrgId;
    if (selectedEventPlace) query.eventPlace = selectedEventPlace;
    if (watchedTag) query.tag = watchedTag;
    if (watchedDistributorId) query.distributorId = watchedDistributorId;
    if (startDate) query.startDate = startDate.format("YYYY-MM-DD");
    if (endDate) query.endDate = endDate.format("YYYY-MM-DD");
    return query;
  }, [
    watchedUserType,
    watchedOrgId,
    selectedEventPlace,
    watchedTag,
    watchedDistributorId,
    startDate,
    endDate,
  ]);

  // Handle download all users based on current tab
  const handleDownloadAll = () => {
    if (isSupport) {
      const supportParams = {};
      if (supportSubscriptionStatus) supportParams.subscriptionStatus = supportSubscriptionStatus;
      if (supportUserStatus) supportParams.status = supportUserStatus;
      if (startDate) supportParams.createdFrom = startDate.format("YYYY-MM-DD");
      if (endDate) supportParams.createdTo = endDate.format("YYYY-MM-DD");
      handleDownloadAllSupportUsers(supportParams);
    } else if (ability.can("Is", "admin") && tabValue === 1) {
      handleDownloadAllUsersWithoutGA(queryParams);
    } else {
      handleDownloadAllUsers(queryParams);
    }
  };

  const isDownloading = isDownloadingUsers || isDownloadingUsersWithoutGA || isDownloadingSupportUsers;

  // Support-specific query params
  const supportQueryParams = useMemo(() => {
    const params = {};
    if (supportSubscriptionStatus) params.subscriptionStatus = supportSubscriptionStatus;
    if (supportUserStatus) params.status = supportUserStatus;
    if (startDate) params.createdFrom = startDate.format("YYYY-MM-DD");
    if (endDate) params.createdTo = endDate.format("YYYY-MM-DD");
    return params;
  }, [supportSubscriptionStatus, supportUserStatus, startDate, endDate]);

  // Static column definitions for the support user table
  // (support API returns flat rows without column metadata)
  const supportColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        minWidth: 180,
        sortable: true,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        valueGetter: (value) => value || "--",
      },
      {
        field: "contactNumber",
        headerName: "Contact Number",
        minWidth: 160,
        sortable: false,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        valueGetter: (value) => value || "--",
      },
      {
        field: "email",
        headerName: "Email",
        minWidth: 220,
        sortable: false,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        valueGetter: (value) => value || "--",
      },
      {
        field: "sponsoredOrganizationName",
        headerName: "Sponsored Org",
        minWidth: 200,
        sortable: false,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        valueGetter: (value) => value || "--",
      },
      {
        field: "state",
        headerName: "State",
        minWidth: 140,
        sortable: false,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        valueGetter: (value) => value || "--",
      },
      {
        field: "city",
        headerName: "City",
        minWidth: 140,
        sortable: false,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        valueGetter: (value) => value || "--",
      },
      {
        field: "isSubscribed",
        headerName: "Subscribed",
        minWidth: 130,
        sortable: false,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        valueFormatter: (value) => (value ? "Yes" : "No"),
        renderCell: (params) => (
          <span
            style={{
              display: "inline-block",
              padding: "2px 10px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600,
              background: params.row.isSubscribed ? "#d4edda" : "#f8d7da",
              color: params.row.isSubscribed ? "#155724" : "#721c24",
            }}
          >
            {params.row.isSubscribed ? "Yes" : "No"}
          </span>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 120,
        sortable: false,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        renderCell: (params) => {
          const color =
            params.value === "active"
              ? { bg: "#d4edda", text: "#155724" }
              : { bg: "#f8d7da", text: "#721c24" };
          return (
            <span
              style={{
                display: "inline-block",
                padding: "2px 10px",
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600,
                background: color.bg,
                color: color.text,
                textTransform: "capitalize",
              }}
            >
              {params.value || "--"}
            </span>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        minWidth: 140,
        sortable: true,
        filterable: false,
        defaultVisible: true,
        isDownloadable: true,
        valueGetter: (value) => value,
        valueFormatter: (value) => {
          if (!value) return "--";
          const datePart = typeof value === "string" && value.includes("T") ? value.split("T")[0] : value;
          const parsed = datePart ? new Date(datePart) : null;
          if (!parsed || Number.isNaN(parsed.getTime())) return "--";
          return parsed.toLocaleDateString("en-GB"); // DD/MM/YYYY
        },
      },
    ],
    [],
  );

  // Handler: trigger support user search by contact number or email
  const handleSupportSearch = async () => {
    const trimmed = supportSearchInput.trim();
    if (!trimmed) return;
    setHasSearched(true);
    setSearchedUser(null);
    const result = await triggerSupportSearch(trimmed);
    // The API may return a single object or an array; handle both
    if (result?.data) {
      const raw = result.data;
      const userData = raw?.data
        ? Array.isArray(raw.data)
          ? raw.data[0] ?? null
          : raw.data
        : Array.isArray(raw)
        ? raw[0] ?? null
        : raw;
      setSearchedUser(userData ?? null);
    } else {
      setSearchedUser(null);
    }
  };

  // Helper: display a value, replacing null/undefined/empty with '-'
  const displayVal = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    return val;
  };

  // Format createdAt date for the detail card
  const formatDate = (val) => {
    if (!val) return "-";
    const datePart = typeof val === "string" && val.includes("T") ? val.split("T")[0] : val;
    const parsed = datePart ? new Date(datePart) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleDateString("en-GB");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Protected>
        <Helmet>
          <title>User</title>
        </Helmet>

        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "flex-start", lg: "center" }}
            justifyContent="space-between"
            spacing={2}
            mb={5}
          >
            <Typography variant="h4" gutterBottom>
              User
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ width: { xs: "100%", lg: "auto" } }}
            >
              <DatePicker
                label="Start Date"
                format="DD/MM/YYYY"
                disableFuture
                maxDate={endDate && dayjs(endDate).isValid() && dayjs(endDate).isBefore(dayjs()) ? endDate : dayjs()}
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: { width: { xs: "100%", sm: 170 } },
                  },
                }}
              />

              <DatePicker
                label="End Date"
                format="DD/MM/YYYY"
                disableFuture
                minDate={startDate && dayjs(startDate).isValid() ? startDate : undefined}
                maxDate={dayjs()}
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: { width: { xs: "100%", sm: 170 } },
                  },
                }}
              />

              {ability.can("post", "Bulk-User") && (
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  onClick={() => setOpenAddBulkUserForm(true)}
                >
                  Add Bulk User
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Iconify icon="eva:download-fill" />
                  )
                }
                onClick={handleDownloadAll}
                disabled={isDownloading}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Download All
              </Button>
            </Stack>
          </Stack>

          {ability.can("post", "Link-User-Code") && (
            <LinkCodeForm
              open={openLinkCodeForm}
              onClose={() => setOpenLinkCodeForm(false)}
              openRow={openRow}
            />
          )}

          {ability.can("post", "Bulk-User") && (
            <BulkAddUserForm
              open={openAddBulkUserForm}
              onClose={() => setOpenAddBulkUserForm(false)}
            />
          )}

          {ability.can("patch", "User") && (
            <UserForm
              open={openUserForm}
              onClose={() => setOpenUserForm(false)}
              openRow={openRow}
              edit
            />
          )}

          {ability.can("post", "Change-Mobile-Number") && (
            <ChangeMobileForm
              open={openChangeMobileForm}
              onClose={() => setOpenChangeMobileForm(false)}
              openRow={openRow}
            />
          )}

          {/* Tabs for With Analytics / Without Analytics */}
          {ability.can("Is", "admin") && (
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Usage" />
                <Tab label="Total Users" />
              </Tabs>
            </Box>
          )}

          {/* Support role: search-first UI — no table shown until contact number or email is searched */}
          {isSupport ? (
            <Card sx={{ p: 3 }}>
              {/* Search bar */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" mb={3}>
                <TextField
                  label="Search by Contact Number or Email"
                  variant="outlined"
                  size="small"
                  value={supportSearchInput}
                  onChange={(e) => setSupportSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSupportSearch(); }}
                  sx={{ flex: 1, minWidth: 260 }}
                  inputProps={{ "data-testid": "supportUserSearch" }}
                />
                <Button
                  variant="contained"
                  onClick={handleSupportSearch}
                  disabled={isSupportSearchFetching || !supportSearchInput.trim()}
                  startIcon={isSupportSearchFetching ? <CircularProgress size={18} color="inherit" /> : <Iconify icon="eva:search-fill" />}
                >
                  Search
                </Button>
              </Stack>

              <Divider sx={{ mb: 3 }} />

              {/* States: idle / loading / error / no result / result */}
              {!hasSearched && (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                  <Iconify icon="eva:person-outline" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">Enter a contact number or email to look up a user.</Typography>
                </Box>
              )}

              {hasSearched && isSupportSearchFetching && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              )}

              {hasSearched && !isSupportSearchFetching && supportSearchError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {supportSearchError?.data?.message || "An error occurred while fetching user details."}
                </Alert>
              )}

              {hasSearched && !isSupportSearchFetching && !supportSearchError && !searchedUser && (
                <Alert severity="info" sx={{ mt: 2 }}>No user found for the given contact number or email.</Alert>
              )}

              {hasSearched && !isSupportSearchFetching && !supportSearchError && searchedUser && (
                <Box>
                  <Typography variant="h6" gutterBottom>User Details</Typography>
                  <Grid container spacing={2}>
                    {[
                      { label: "Name", value: displayVal(searchedUser.name) },
                      { label: "Contact Number", value: displayVal(searchedUser.contactNumber) },
                      { label: "Email", value: displayVal(searchedUser.email) },
                      { label: "Sponsored Organisation", value: displayVal(searchedUser.sponsoredOrganizationName) },
                      { label: "State", value: displayVal(searchedUser.state) },
                      { label: "City", value: displayVal(searchedUser.city) },
                      { label: "Status", value: displayVal(searchedUser.status) },
                      { label: "Subscribed", value: displayVal(searchedUser.isSubscribed) },
                      { label: "Created At", value: formatDate(searchedUser.createdAt) },
                    ]
                      .concat(
                        Object.entries(searchedUser)
                          .filter(([key]) =>
                            ![
                              "name", "contactNumber", "email", "sponsoredOrganizationName",
                              "state", "city", "status", "isSubscribed", "createdAt",
                            ].includes(key)
                          )
                          .map(([key, val]) => ({
                            label: key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()),
                            value: displayVal(val),
                          }))
                      )
                      .map((field) => (
                        <Grid item xs={12} sm={6} md={4} key={field.label}>
                          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "background.neutral" }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {field.label}
                            </Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-word" }}>
                              {field.value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}
            </Card>
          ) : (
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 280px)",
              minHeight: 480,
              overflow: "hidden",
            }}
          >
            <Scrollbar sx={{ flex: 1, overflow: "auto" }}>
              <CustomTable
                key={ability.can("Is", "admin") ? tabValue : 0} // Force re-render when tab changes (admin only)
                canSelect
                useDataQuery={
                  ability.can("Is", "admin") && tabValue === 1
                    ? useGetUsersWithoutGAQuery
                    : useGetUsersQuery
                }
                searchLabel="Search User..."
                tableName={
                  ability.can("Is", "admin") && tabValue === 1
                    ? "UserWithoutGA"
                    : "UserWithGA"
                }
                isSupport={false}
                defaultQueryParams={queryParams}
                columnsOverride={null}
                restrictDownloadCols={["otp", "isSubscribe"]}
                searchCols={[
                  "contactNumber",
                  "name",
                  "deviceCode",
                  "recipientOrganizationName",
                  "senderOrganizationName",
                ]}
                resetParentForm={handleResetFilter}
                actions={
                  ability.can("patch", "User") ||
                  ability.can("get", "User-Google-Analytics") ||
                  ability.can("post", "Link-User-Code") ||
                  ability.can("post", "Deink-User-Code") ||
                  ability.can("post", "Change-Mobile-Number")
                    ? {
                        field: "actions",
                        headerName: "",
                        type: "actions",
                        getActions: ({ row }) =>
                          [
                            ability.can("patch", "User") ? (
                              <GridActionsCellItem
                                key={0}
                                icon={<Edit />}
                                label="Edit"
                                showInMenu
                                onClick={() => {
                                  setOpenRow(row);
                                  setOpenUserForm(true);
                                }}
                              />
                            ) : null,
                            ability.can("get", "User-Google-Analytics") ? (
                              <GridActionsCellItem
                                key={1}
                                icon={
                                  <Iconify
                                    icon="eva:eye-fill"
                                    sx={{ color: "primary.main" }}
                                  />
                                }
                                sx={{
                                  color: "primary.main",
                                }}
                                label="Usage"
                                showInMenu
                                onClick={() => {
                                  navigate(`/dashboard/user/${row?.id}`);
                                }}
                              />
                            ) : null,
                            ability.can("post", "Link-User-Code") &&
                            !row?.isCodeVerified ? (
                              <GridActionsCellItem
                                key={2}
                                icon={
                                  <Iconify
                                    icon="eva:link-2-fill"
                                    sx={{ color: "success.main" }}
                                  />
                                }
                                sx={{
                                  color: "success.main",
                                }}
                                label="Link Code"
                                showInMenu
                                onClick={() => {
                                  setOpenRow(row);
                                  handleLinkCode();
                                }}
                              />
                            ) : null,
                            ability.can("post", "Delink-User-Code") &&
                            row?.isCodeVerified ? (
                              <GridActionsCellItem
                                key={3}
                                icon={
                                  unlinkCodeLoading ? (
                                    <CircularProgress
                                      size={20}
                                      sx={{
                                        color: "warning.main",
                                      }}
                                    />
                                  ) : (
                                    <LinkOff
                                      sx={{
                                        color: "warning.main",
                                      }}
                                    />
                                  )
                                }
                                sx={{
                                  color: "warning.main",
                                }}
                                label="Unlink Code"
                                showInMenu
                                onClick={async () => {
                                  setOpenRow(row);
                                  setOpenDelinkConfirmation(true);
                                }}
                              />
                            ) : null,
                            ability.can("post", "Change-Mobile-Number") ? (
                              <GridActionsCellItem
                                key={4}
                                icon={
                                  <Iconify
                                    icon="eva:phone-fill"
                                    sx={{ color: "info.main" }}
                                  />
                                }
                                sx={{
                                  color: "info.main",
                                }}
                                label="Change Mobile"
                                showInMenu
                                onClick={() => {
                                  setOpenRow(row);
                                  setOpenChangeMobileForm(true);
                                }}
                              />
                            ) : null,
                          ].filter((action) => action !== null),
                      }
                    : null
                }
                extraHeaderField={
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      mt: 1,
                    }}
                  >
                    <Grid container spacing={1} flexBasis="100%">

                      {/* UserType Organization (non-support) */}
                      {!isSupport &&
                        (ability.can("Is", "organization") ||
                          ability.can("Is", "admin") ||
                          ability.can("Is", "support")) && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <FormControl fullWidth>
                            <InputLabel id="userType-label">
                              User Type
                            </InputLabel>
                            <Select
                              labelId="userType-label"
                              id="userType-select"
                              {...register("userType")}
                              label="User Type"
                              value={watch("userType") ?? ""}
                              inputProps={{ "data-testid": "userType" }}
                            >
                              <MenuItem value="all">All</MenuItem>
                              <MenuItem value="device">Device Users</MenuItem>
                              <MenuItem value="subscription">
                                Premium Users
                              </MenuItem>
                              <MenuItem value="free">Free Users</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {/* Organization Select (non-support) */}
                      {!isSupport && ability.can("get", "Organizations") && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <FormControl fullWidth>
                            <InputLabel id="org-select-label">
                              Organization
                            </InputLabel>
                            <Select
                              {...register("organizationId")}
                              labelId="org-select-label"
                              id="org-select"
                              label="Organization"
                              inputProps={{ "data-testid": "organizationId" }}
                              value={watch("organizationId") ?? ""}
                            >
                              <MenuItem value="">Select Organization</MenuItem>
                              {isOrgLoading || isOrgFetching ? (
                                <MenuItem disabled>
                                  <CircularProgress size={24} />
                                </MenuItem>
                              ) : (
                                organizations?.data?.map((org) => (
                                  <MenuItem key={org.id} value={org.id}>
                                    {org.name}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {/* Event Place Select (non-support) */}
                      {!isSupport && ability.can("get", "All-Event-Place") && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <FormControl fullWidth>
                            <InputLabel id="eventPlace-label">
                              Event Place
                            </InputLabel>
                            <Select
                              labelId="eventPlace-label"
                              id="eventPlace-select"
                              {...register("eventPlace")}
                              label="Event Place"
                              value={selectedEventPlace}
                              onChange={handleEventPlaceChange}
                              inputProps={{ "data-testid": "eventPlace" }}
                            >
                              <MenuItem value="">Select Event Place</MenuItem>
                              {isLoadingEventPlaces ? (
                                <MenuItem disabled>
                                  <CircularProgress size={24} />
                                </MenuItem>
                              ) : (
                                getFilteredEventPlaces().map((place) => (
                                  <MenuItem
                                    key={place.id}
                                    value={place.eventPlace}
                                  >
                                    {place.eventPlace}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {/* Tag Select (non-support) */}
                      {!isSupport && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <FormControl fullWidth>
                            <InputLabel id="tag-label">Tag</InputLabel>
                            <Select
                              labelId="tag-label"
                              id="tag-select"
                              label="Tag"
                              value={watch("tag") ?? ""}
                              onChange={(e) => setValue("tag", e.target.value)}
                              disabled={
                                !selectedEventPlace || filteredTags.length === 0
                              }
                              inputProps={{ "data-testid": "tag" }}
                            >
                              {filteredTags.length > 0 ? (
                                filteredTags.map((tag) => (
                                  <MenuItem key={tag} value={tag}>
                                    {tag}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem disabled>
                                  No tags available for this event place
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {/* Date Pickers */}
                    </Grid>
                  </Box>
                }
              />
            </Scrollbar>
          </Card>
          )}{/* end isSupport ternary */}
        </Container>

        {ability.can("post", "Delink-User-Code") && (
          <CustomDeleteConfirmation
            open={openDelinkConfirmation}
            onClose={() => setOpenDelinkConfirmation(false)}
            row={openRow}
            nameKey="deviceCode"
            nameTitle={"Device Code"}
            title="Unlink User"
            description={`Are you sure you want to unlink user with device code "${openRow?.deviceCode}" and contact number ${openRow?.contactNumber}?`}
            confirmText="Unlink"
            confirmTextColor="error"
            isLoading={unlinkCodeLoading}
            deleteFunc={delink}
          />
        )}
      </Protected>
    </LocalizationProvider>
  );
}